import { DndContext } from "@dnd-kit/core";
import { restrictToHorizontalAxis } from "@dnd-kit/modifiers";
import { useLocalstorage } from "@hooks";
import { TiredPenguin } from "@illustrations";
import { Table as TanStackTable } from "@tanstack/react-table";
import { FilterableFields } from "@utils";
import { TFunction } from "i18next";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { Switch } from "../../controls/switch";
import { PaginationInfo } from "../../types";
import { TableContextProvider } from "./context";
import TableControls, { FilterOption } from "./controls";
import type { FilterMenuItem } from "./filter-v2/menu/types";
import TableHeading from "./heading";
import TableRows from "./rows";

interface TableProps {
  table: TanStackTable<any>;
  t: TFunction<"translation", undefined>;
  tableId?: string;
  newRecordModalTitle?: string;
  newRecordModalDescription?: string;
  selectable?: boolean;
  isLoading?: boolean;
  refresh?: () => void;
  refreshing?: boolean;
  NewForm?: ({
    toggleOpen,
  }: {
    toggleOpen: (newOpen: boolean) => void;
  }) => JSX.Element;
  newFormLink?: string;
  onPageChange?: (newPage: number) => void;
  pagination?: PaginationInfo;
  tableTitle?: string;
  titleHeadingLevel?: 1 | 2 | 3 | 4 | 5 | 6;
  tableHeadingClassName?: string;
  extraControls?: React.ReactNode;
  subheading?: React.ReactNode;
  filterableFields?: FilterableFields;
  baseFieldLabelTranslations?: string;
  filterMenuItemGroups?: FilterMenuItem[][];
}

export type {
  BooleanCompoundCondition,
  CompoundCondition,
  CompoundFilterGroup,
  DateCompoundCondition,
  FilterMenuItem,
  LogicalOperator,
  NumericCompoundCondition,
  TextCompoundCondition,
} from "./filter-v2/menu/types";
export { default as TableFilterPopover } from "./filter-v2/filter-popover";
export { TableFilterBar } from "./filter-v2/table-filter-bar";
export * from "./utils";

const META_WIDTH_TO_PX: Record<string, number> = {
  fit: 100,
  small: 80,
};
const DEFAULT_COLUMN_WIDTH = 150;
const MIN_COLUMN_WIDTH = 60;

function getDefaultColumnWidths(
  table: TanStackTable<any>,
): Record<string, number> {
  const widths: Record<string, number> = {};
  for (const column of table.getAllColumns()) {
    const meta = column.columnDef.meta as { width?: string } | undefined;
    widths[column.id] =
      META_WIDTH_TO_PX[meta?.width ?? ""] ?? DEFAULT_COLUMN_WIDTH;
  }
  return widths;
}

export function Table({
  table,
  refresh,
  refreshing = false,
  NewForm,
  newFormLink,
  newRecordModalTitle,
  newRecordModalDescription,
  t,
  tableId,
  isLoading = false,
  onPageChange = () => null,
  selectable = false,
  pagination = undefined,
  tableTitle = "",
  titleHeadingLevel = 1,
  tableHeadingClassName = "text-xl font-semibold text-gray-800",
  extraControls = null,
  subheading = null,
  filterableFields = {},
  baseFieldLabelTranslations = "",
  filterMenuItemGroups,
}: TableProps) {
  const totalSelected = table.getSelectedRowModel().rows.length;
  const {
    current_page: currentPage,
    total_pages: totalPages,
    total: totalItems,
  } = pagination || {
    current_page: 1,
    total_pages: 1,
    total: 0,
  };

  const [editMode, setEditMode] = useState(false);
  const [columnWidths, setColumnWidths] = useState<Record<string, number>>(() =>
    getDefaultColumnWidths(table),
  );

  const columnsRef = useRef(table.getAllColumns());
  useEffect(() => {
    const currentColumns = table.getAllColumns();
    if (currentColumns.length !== columnsRef.current.length) {
      columnsRef.current = currentColumns;
      setColumnWidths((prev) => {
        const updated = { ...prev };
        for (const column of currentColumns) {
          if (updated[column.id] === undefined) {
            const meta = column.columnDef.meta as
              { width?: string } | undefined;
            updated[column.id] =
              META_WIDTH_TO_PX[meta?.width ?? ""] ?? DEFAULT_COLUMN_WIDTH;
          }
        }
        return updated;
      });
    }
  }, [table.getAllColumns()]);

  const onColumnResize = useCallback((columnId: string, delta: number) => {
    setColumnWidths((prev) => ({
      ...prev,
      [columnId]: Math.max(
        MIN_COLUMN_WIDTH,
        (prev[columnId] ?? DEFAULT_COLUMN_WIDTH) + delta,
      ),
    }));
  }, []);

  const { updateData, sortableFields } = table?.options?.meta ?? {};

  const columns = table.getAllColumns();
  const filterOptions = useMemo(() => {
    const res: FilterOption[] = [];
    columns.forEach((column) => {
      const meta = column.columnDef.meta as { filter?: FilterOption };

      if (meta?.filter) {
        res.push(meta.filter);
      }
    });

    return res;
  }, [columns]);

  const [preservedColumnOrder, setPreservedColumnOrder] = useLocalstorage(
    `TABLE-COLUMN-ORDER-${tableId?.toUpperCase() || ""}`,
    JSON.stringify(table.getState().columnOrder),
  );

  useEffect(() => {
    let initialOrder = [];
    try {
      initialOrder = JSON.parse(preservedColumnOrder || "[]");
    } catch (error) {
      console.error("Failed to parse preserved column order:", error);
    }

    let columnOrder = table.getState().columnOrder;
    if (columnOrder.length === 0) {
      columnOrder = columns.map((col) => col.id);
    }

    const updatedOrder = [
      ...initialOrder,
      ...columnOrder.filter((col) => !initialOrder.includes(col)),
    ];

    table.setColumnOrder(updatedOrder);
  }, [preservedColumnOrder, table, columns]);

  const handleDragEnd = (event: any) => {
    const { active, over } = event;

    const draggedId = active.id;
    const targetId = over?.id;

    if (!draggedId || !targetId || draggedId === targetId) {
      return;
    }

    const oldOrder = table.getState().columnOrder;
    const newOrder = [...oldOrder];
    const draggedIndex = newOrder.indexOf(draggedId);
    const targetIndex = newOrder.indexOf(targetId);

    newOrder.splice(draggedIndex, 1);
    newOrder.splice(targetIndex, 0, draggedId);

    if (JSON.stringify(oldOrder) !== JSON.stringify(newOrder)) {
      setPreservedColumnOrder(JSON.stringify(newOrder));
    }
  };

  return (
    <TableContextProvider
      value={{
        tableId: tableId || "",
        t,
        table,
        sortableFields,
        filterableFields,
        baseFieldLabelTranslations,
        editMode,
        setEditMode,
        columnWidths,
        onColumnResize,
      }}
    >
      <div className="h-full grid grid-rows-[auto_1fr_auto] overflow-hidden">
        <TableControls
          table={table}
          tableTitle={tableTitle}
          headingLevel={titleHeadingLevel}
          tableHeadingClassName={tableHeadingClassName}
          newRecordModalTitle={newRecordModalTitle}
          newRecordModalDescription={newRecordModalDescription}
          refresh={refresh}
          isRefreshing={refreshing}
          totalSelected={totalSelected}
          NewForm={NewForm}
          newFormLink={newFormLink}
          filterOptions={filterOptions}
          filterMenuItemGroups={filterMenuItemGroups}
          extraControls={extraControls}
          subheading={subheading}
        />
        <div className="w-full overflow-auto flex flex-col">
          <DndContext
            onDragEnd={handleDragEnd}
            modifiers={[restrictToHorizontalAxis]}
          >
            <table
              className="w-full rounded-lg border-collapse overflow-hidden"
              style={{
                tableLayout: "fixed",
                minWidth:
                  Object.values(columnWidths).reduce((sum, w) => sum + w, 0) +
                  (selectable ? 40 : 0),
              }}
            >
              <TableHeading selectable={selectable} />
              {!isLoading && (
                <TableRows
                  rows={table.getRowModel().rows}
                  selectable={selectable}
                />
              )}
            </table>
          </DndContext>
          {isLoading && (
            <div className="w-full text-center my-3">Loading...</div>
          )}
          {!isLoading && table.getRowModel().rows.length === 0 && (
            <div className="w-full flex-grow text-center my-3 flex justify-center items-center">
              <div className="w-full max-w-[350px] flex-grow">
                <TiredPenguin />
                <p>{t("emptyTableMessage")}</p>
              </div>
            </div>
          )}
        </div>
        <div className="py-2 flex flex-col md:flex-row items-center justify-between">
          <div className="flex flex-row gap-4 items-center font-light text-sm">
            {!!pagination && (
              <div>
                {totalItems}{" "}
                {totalItems === 1 ? t("paginationItem") : t("paginationItems")}{" "}
                | Page {currentPage} of {totalPages}
              </div>
            )}
            {!!updateData && (
              <div className="flex flex-row gap-2 items-center">
                <label>{t("editMode")}</label>
                <Switch
                  name="editable-table"
                  checked={editMode}
                  onCheckedChange={(checked) => setEditMode(checked)}
                />
              </div>
            )}
          </div>
          {!!pagination && (
            <div>
              <button
                onClick={() => onPageChange(currentPage - 1)}
                disabled={currentPage <= 1}
                className="disabled:cursor-not-allowed mr-2 text-primary-400"
              >
                prev
              </button>
              <button
                onClick={() => onPageChange(currentPage + 1)}
                disabled={currentPage >= totalPages}
                className="disabled:cursor-not-allowed text-primary-400"
              >
                next
              </button>
            </div>
          )}
        </div>
      </div>
    </TableContextProvider>
  );
}
